import {Checkbox, Dialog, MenuItem, Select} from "@mui/material";
import {useEffect, useState} from "react";
import {
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    EnumField,
    TextField,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../../CreateAnotherCheckBox";
import ErrorHelper from "../../../utils/ErrorHelper";
import {showNotification} from "@/actions";
import {Link} from "../../widgets";
import {useBoolState} from "@/hooks";
import {DomainType, PrivateRoutingType} from "@/api";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {useQuery} from "@apollo/client";
import {UserPackagesDocument, UserPackagesQuery} from "@/components/domain/create/queries.generated";
import CreateDomainForm from "./CreateDomainForm";
import {DOMAIN_TYPE_MESSAGES, PRIVATE_ROUTING_TYPE_MESSAGES} from "@/utils/translations";

export type Props = {
    onSuccess: () => void;
    close: () => void;
    isOpen: boolean;
    title: MessageDescriptor;
    successItemPath: string;
};

export default ({onSuccess, isOpen, close, title, successItemPath}: Props) => {
    const form = new CreateDomainForm(
        (_form) =>
            showNotification(
                defineMessage({
                    id: "domainCreated",
                    defaultMessage: "Domain {link} has been successfully created.",
                }),
                "success",
                {
                    link: (
                        <Link to={`/${successItemPath}/${_form.result.data.createDomain.id}`}>{_form.values.name}</Link>
                    ),
                },
                10000,
            ),
        (_) =>
            showNotification(
                defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}),
                "error",
                null,
                10000,
            ),
    );

    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);
    const [enabled, enable, disable] = useBoolState(true);
    const structureConfig = useStructureConfig();

    const userPackagesQuery = useQuery<UserPackagesQuery>(UserPackagesDocument, {
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (!userPackagesQuery.loading) {
            if (userPackagesQuery.data.userPackages && form.values.defaultUserPackage == undefined) {
                form.updateValues({defaultUserPackage: userPackagesQuery.data.userPackages[0]?.name});
            }
        }
        // form can not be a dependency because it will cause an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPackagesQuery.loading, userPackagesQuery.data]);

    const userPackages = userPackagesQuery.data
        ? userPackagesQuery.data?.userPackages.map((userPackage) => userPackage.name)
        : [];

    function handleClose() {
        close();
        form.reset();
    }

    async function submit() {
        disable();
        if (await form.submit()) {
            onSuccess();

            if (createAnother) form.reset();
            else handleClose();

            enable();
        } else {
            enable();
            return;
        }
    }

    return (
        <Dialog open={isOpen} fullWidth={true} maxWidth={"md"}>
            <FormattedDialogTitle title={title} />
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.domainType", defaultMessage: "Domain type"})}
                    >
                        <EnumField
                            value={form.values.domainType}
                            enum_={{DomainType}}
                            onChange={(val) =>
                                form.updateValues({
                                    domainType: val as DomainType,
                                })
                            }
                            disabled={!enabled || !structureConfig.isEnabledFeature("residentialDomains")}
                            translations={DOMAIN_TYPE_MESSAGES}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.name", defaultMessage: "Name"})}
                        required
                    >
                        <TextField
                            value={form.values.name}
                            onChange={(value) => form.updateValues({name: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("name")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.description", defaultMessage: "Description"})}
                    >
                        <TextField
                            value={form.values.description}
                            onChange={(value) => form.updateValues({description: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("description")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.dialoutPrefix", defaultMessage: "Dialout prefix"})}
                    >
                        <TextField
                            value={form.values.dialoutPrefix}
                            onChange={(value) => form.updateValues({dialoutPrefix: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("dialoutPrefix")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.specialNumbersDialoutPrefix",
                            defaultMessage: "Special numbers dialout prefix",
                        })}
                    >
                        <TextField
                            value={form.values.specialNumbersDialoutPrefix}
                            onChange={(value) => form.updateValues({specialNumbersDialoutPrefix: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("specialNumbersDialoutPrefix")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.shortNumbersLength",
                            defaultMessage: "Short numbers length",
                        })}
                        required
                    >
                        <TextField
                            value={form.values.shortNumbersLength}
                            onChange={(value) => form.updateValues({shortNumbersLength: parseInt(value)})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("shortNumbersLength")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.stationNames", defaultMessage: "Station names"})}
                    >
                        {form.values.preferDisplayNamesForStationNames}
                        <Select
                            value={form.values.preferDisplayNamesForStationNames ? 1 : 0}
                            onChange={(ev) =>
                                form.updateValues({preferDisplayNamesForStationNames: Boolean(ev.target.value)})
                            }
                            disabled={!enabled}
                            margin="dense"
                            variant="outlined"
                            fullWidth={true}
                        >
                            <MenuItem key={0} value={0} id="domain-station-name-use-e164">
                                <FormattedMessage id="use_e164" defaultMessage="Use E.164 number" />
                            </MenuItem>
                            <MenuItem key={1} value={1} id="domain-station-name-use-display-name">
                                <FormattedMessage id="use_display_name" defaultMessage="Use display name" />
                            </MenuItem>
                        </Select>
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "domainSettings.locationName", defaultMessage: "Location name"})}
                        required
                    >
                        <TextField
                            value={form.values.locationName}
                            onChange={(value) => form.updateValues({locationName: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("locationName")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.locationDescription",
                            defaultMessage: "Location description",
                        })}
                        required
                    >
                        <TextField
                            value={form.values.locationDescription}
                            onChange={(value) => form.updateValues({locationDescription: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("locationDescription")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.locationPilotNumber",
                            defaultMessage: "Location pilot number",
                        })}
                        required
                    >
                        <TextField
                            value={form.values.locationPilotNumber}
                            onChange={(value) => form.updateValues({locationPilotNumber: value.toString()})}
                            disabled={!enabled}
                            errors={errorHelper.fieldErrors("locationPilotNumber")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.smtpAuthEnabled",
                            defaultMessage: "Allow SMTP authentication",
                        })}
                    >
                        <Checkbox
                            checked={form.values.smtpAuthEnabled}
                            onChange={() => form.updateValues({smtpAuthEnabled: !form.values.smtpAuthEnabled})}
                            disabled={!enabled}
                        />
                    </FormattedFormRow>
                    {form.values.smtpAuthEnabled && (
                        <>
                            <FormattedFormRow
                                label={defineMessage({
                                    id: "domainSettings.smtpUsername",
                                    defaultMessage: "SMTP username",
                                })}
                            >
                                <TextField
                                    value={form.values.smtpUsername}
                                    onChange={(value) => form.updateValues({smtpUsername: value.toString()})}
                                    disabled={!enabled}
                                />
                            </FormattedFormRow>
                            <FormattedFormRow
                                label={defineMessage({
                                    id: "domainSettings.smtpUserpass",
                                    defaultMessage: "SMTP password",
                                })}
                            >
                                <TextField
                                    type="password"
                                    value={form.values.smtpUserpass}
                                    onChange={(value) => form.updateValues({smtpUserpass: value.toString()})}
                                    disabled={!enabled}
                                />
                            </FormattedFormRow>
                        </>
                    )}
                    <FormattedFormRow
                        label={defineMessage({
                            id: "domainSettings.privateRoutingType",
                            defaultMessage: "Custom routing type",
                        })}
                    >
                        <EnumField
                            value={form.values.privateRoutingType}
                            enum_={{PrivateRoutingType}}
                            onChange={(val) =>
                                form.updateValues({
                                    privateRoutingType: val as PrivateRoutingType,
                                })
                            }
                            disabled={!enabled}
                            translations={PRIVATE_ROUTING_TYPE_MESSAGES}
                        />
                    </FormattedFormRow>
                    {userPackages && (
                        <FormattedFormRow
                            label={defineMessage({
                                id: "domainSettings.defaultUserPackage",
                                defaultMessage: "Default user package",
                            })}
                        >
                            <Select
                                value={form.values.defaultUserPackage}
                                onChange={(ev) => form.updateValues({defaultUserPackage: ev.target.value?.toString()})}
                                disabled={!enabled}
                                margin="dense"
                                variant="outlined"
                                fullWidth={true}
                            >
                                {userPackages.map((userPackage, idx) => (
                                    <MenuItem key={idx} value={userPackage} disabled={!enabled}>
                                        {userPackage}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormattedFormRow>
                    )}
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={close}
                    mainButtonEnabled={form.hasChanged}
                    secondaryButtonEnabled={form.hasChanged}
                />
            </DialogActions>
        </Dialog>
    );
};
